import {compact} from '@benchling/js-utils';
import {css, StyleSheet, StyleDeclarationValue} from 'aphrodite';
import React from 'react';
import {Handle, Position} from 'reactflow';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import navigateToURL from 'lib/navigateToURL';
import ProcessNodeStatusCountDisplay from 'processes/graph-builder-processes/flowchart-viz/ProcessNodeStatusCountDisplay';
import {GRAY, GRAY_DARKER, RED} from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ElementMouseEvent} from 'util/Types';

import ProcessFlowchartBaseNodeRenderer from './ProcessFlowchartBaseNodeRenderer';
import {ProcessFlowchartNodeType, ProcessFlowchartStaticNode} from './ProcessFlowchartVizConstants';

interface FlowchartTaskNodeProps {
  data: ProcessFlowchartStaticNode;
  type: string; // This value is passed thru ReactFlow, so we lose its provenance as an enum
  nodeStyles?: Array<StyleDeclarationValue>;
}

const ErrorIcon = ({errorMessage}: {errorMessage: string}): JSX.Element => (
  <div className={css(styles.errorIconWrapper)}>
    <Tooltip text={errorMessage} placement={FloatingPlacement.TOP}>
      <BenchlingIcon icon="exclamation" className={css(styles.errorIcon)} />
    </Tooltip>
  </div>
);

function hasTargetHandle(type: ProcessFlowchartNodeType, node: ProcessFlowchartStaticNode): boolean {
  return [ProcessFlowchartNodeType.END_NODE, ProcessFlowchartNodeType.MIDDLE_NODE].includes(type);
}

function hasSourceHandle(type: ProcessFlowchartNodeType, node: ProcessFlowchartStaticNode): boolean {
  return [ProcessFlowchartNodeType.START_NODE, ProcessFlowchartNodeType.MIDDLE_NODE].includes(type);
}

export default function ProcessFlowchartTaskNodeRenderer({
  data,
  type,
  nodeStyles,
}: FlowchartTaskNodeProps): JSX.Element {
  const nodeType = type as ProcessFlowchartNodeType;
  assert(nodeType in ProcessFlowchartNodeType, 'Unknown flowchart node type', {type, data});
  const onClick = (e: ElementMouseEvent): void => {
    if (data.linkUrl) {
      analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.CLICKED_FLOWCHART_GRAPH_NODE, {
        flowchart_task_group_api_id: data.analyticsData?.rootProcessId ?? null,
      });
      // Prevent ReactFlow from responding to this click
      e.stopPropagation();
      e.preventDefault();
      if (data.onClick) {
        data.onClick();
      } else {
        navigateToURL(data.linkUrl);
      }
    }
  };

  const errorMessage = (data.errors?.length ?? 0) > 0 ? _.unique(data.errors!).join('\n') : null;

  const styleDeclarations = compact([data.linkUrl ? styles.linked : null, ...(nodeStyles ?? [])]);

  const sourceHandleClass = css(handleStyles.base, handleStyles.hidden, handleStyles.hiddenSource);
  const targetHandleClass = css(handleStyles.base, handleStyles.hidden, handleStyles.hiddenTarget);

  return (
    <ProcessFlowchartBaseNodeRenderer
      data={data}
      nodeStyles={styleDeclarations}
      onClick={onClick}
      showTooltip={true}
      isStatic={true}
    >
      {errorMessage && <ErrorIcon errorMessage={errorMessage} />}
      <ProcessNodeStatusCountDisplay statusCounts={data.statusCounts ?? []} />
      {hasTargetHandle(nodeType, data) && (
        <Handle type="target" position={Position.Top} className={targetHandleClass} />
      )}
      {hasSourceHandle(nodeType, data) && (
        <Handle type="source" position={Position.Bottom} className={sourceHandleClass} />
      )}
    </ProcessFlowchartBaseNodeRenderer>
  );
}

const styles = StyleSheet.create({
  linked: {
    ':hover': {
      cursor: 'pointer',
      border: `1px solid rgba(92,124,131,.8)`,
      boxShadow: `0 0 8px ${GRAY}`,
    },
  },
  errorIcon: {
    color: RED,
    width: '12px',
    height: '12px',
  },
  errorIconWrapper: {
    position: 'absolute',
    left: '6px',
    top: '6px',
  },
  movableNode: {
    cursor: 'move',
  },
});

const handleStyles = StyleSheet.create({
  base: {
    width: '7px',
    height: '7px',
    background: GRAY_DARKER,
    borderColor: 'rgba(0,0,0,0)',
  },
  editableSource: {
    top: 'calc(100% - 4px)',
  },
  editableTarget: {
    top: -4,
  },
  hidden: {
    opacity: 0,
    cursor: 'default',
  },
  hiddenSource: {
    top: 'calc(100% - 6px)',
  },
  hiddenTarget: {
    top: -1,
  },
});

export const PROCESS_FLOWCHART_NODE_TYPE_TO_TASK_NODE_RENDERER = {
  [ProcessFlowchartNodeType.START_NODE]: ProcessFlowchartTaskNodeRenderer,
  [ProcessFlowchartNodeType.MIDDLE_NODE]: ProcessFlowchartTaskNodeRenderer,
  [ProcessFlowchartNodeType.END_NODE]: ProcessFlowchartTaskNodeRenderer,
  [ProcessFlowchartNodeType.DISCONNECTED_NODE]: ProcessFlowchartTaskNodeRenderer,
};
